import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';

type Filters = {
  [key: string]: unknown;
  search?: string;
};

/**
 * Checks if a given value is considered empty.
 * A value is empty if it is undefined, null, an empty string, or an empty array.
 *
 * @param value - The value to check for emptiness.
 * @returns True if the value is empty, otherwise false.
 */
function isEmptyValue(value: unknown) {
  if (value === undefined || value === null) {
    return true;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }

  if (Array.isArray(value) && value.map(Boolean).length === 0) {
    return true;
  }

  return false;
}

/**
 * Custom hook for managing URL-based filters.
 *
 * This hook provides a way to synchronize filter state with URL parameters,
 * allowing for easy navigation and state management based on the URL.
 *
 * @template T - The type of filters being managed.
 * @returns An object containing the current filters and a function to update them.
 */
export function useUrlFilters<T extends Filters>(
  defaultFilters: Partial<T> = {},
) {
  const { page, ...filters } = useSearch({ strict: false });

  const navigate = useNavigate();

  const setFilters = (newFilters: Partial<T>) => {
    navigate({
      search(previous) {
        // Start with current filters
        const updatedFilters = { ...previous };

        // Update with new filters, removing empty values
        for (const [key, value] of Object.entries(newFilters)) {
          if (isEmptyValue(value)) {
            // Using computed property access is safer than delete
            updatedFilters[key] = undefined;
          } else {
            updatedFilters[key] = value as string | undefined;
          }
        }

        return updatedFilters;
      },
    });
  };

  useEffect(() => {
    if (Object.keys(defaultFilters).length > 0) {
      // To make sure this fires after any other useEffect that might be setting the filters on mount
      setTimeout(() => {
        setFilters(defaultFilters);
      }, 0);
    }
  }, []);

  return {
    filters: filters as T,
    setFilters,
  };
}
